import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  CheckCircle,
  Video,
  Calendar,
  Clock,
  RefreshCw,
  AlertCircle,
  CalendarPlus,
  Download,
} from 'lucide-react'
import { bookingApi } from '../../lib/api/sessions'
import LoadingSpinner from '../../components/ui/LoadingSpinner'

interface BookingItem {
  booking_id: number
  status: string
  scheduled_start_utc: string
  duration_minutes: number
  interviewer_alias: string | null
  zoom_join_url: string | null
  zoom_password: string | null
}

// ── Calendar helpers ─────────────────────────────────────────────────────────

function toGCalDate(iso: string): string {
  // Returns YYYYMMDDTHHmmssZ
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function buildGoogleCalendarUrl(b: BookingItem): string {
  const start = toGCalDate(b.scheduled_start_utc)
  const endMs = new Date(b.scheduled_start_utc).getTime() + b.duration_minutes * 60000
  const end = toGCalDate(new Date(endMs).toISOString())

  let details = 'Your mock interview session on Interview Dress Rehearsal.'
  if (b.zoom_join_url) details += `\n\nZoom Join URL: ${b.zoom_join_url}`
  if (b.zoom_password) details += `\nMeeting Password: ${b.zoom_password}`
  details += '\n\nPowered by Interview Dress Rehearsal'

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: `Mock Interview${b.interviewer_alias ? ` with ${b.interviewer_alias}` : ''} — IDR`,
    dates: `${start}/${end}`,
    details,
    location: b.zoom_join_url ?? 'Zoom Meeting',
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

function downloadICS(b: BookingItem): void {
  const start = toGCalDate(b.scheduled_start_utc).replace('Z', '')
  const endMs = new Date(b.scheduled_start_utc).getTime() + b.duration_minutes * 60000
  const end = toGCalDate(new Date(endMs).toISOString()).replace('Z', '')
  const now = toGCalDate(new Date().toISOString()).replace('Z', '')

  const summary = `Mock Interview${b.interviewer_alias ? ` with ${b.interviewer_alias}` : ''} — IDR`
  let description = 'Your mock interview session on Interview Dress Rehearsal.'
  if (b.zoom_join_url) description += `\\nZoom Join URL: ${b.zoom_join_url}`
  if (b.zoom_password) description += `\\nMeeting Password: ${b.zoom_password}`

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Interview Dress Rehearsal//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${start}Z`,
    `DTEND:${end}Z`,
    `DTSTAMP:${now}Z`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `LOCATION:${b.zoom_join_url ?? 'Zoom Meeting'}`,
    `UID:idr-booking-${b.booking_id}@interviewdressrehearsal.com`,
    'BEGIN:VALARM',
    'TRIGGER:-PT30M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 30 minutes',
    'END:VALARM',
    'BEGIN:VALARM',
    'TRIGGER:-PT10M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Interview in 10 minutes',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n')

  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `interview-${b.booking_id}.ics`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ─────────────────────────────────────────────────────────────────────────────

export default function ClientPaymentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id') || ''

  const [booking, setBooking] = useState<BookingItem | null>(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)

  const fetchBooking = async () => {
    try {
      const data = await bookingApi.getMySchedule()
      const bookings: BookingItem[] = data.bookings || []
      // Find the most recent booking that was just paid (confirmed or still pending_payment)
      const recent = bookings
        .filter((b) => ['confirmed', 'pending_payment'].includes(b.status))
        .sort(
          (a, b) =>
            new Date(b.scheduled_start_utc).getTime() -
            new Date(a.scheduled_start_utc).getTime()
        )[0]
      setBooking(recent || null)
    } catch (err) {
      console.error('Failed to fetch bookings:', err)
    } finally {
      setLoading(false)
      setChecking(false)
    }
  }

  useEffect(() => {
    const init = async () => {
      // If Stripe returned a session_id, verify payment and confirm booking first
      if (sessionId) {
        try {
          await bookingApi.verifyCheckoutSession(sessionId)
        } catch (err) {
          console.error('Checkout verification error:', err)
        }
      }
      await fetchBooking()
    }
    init()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setChecking(true)
    fetchBooking()
  }

  if (loading) {
    return <LoadingSpinner message="Loading your booking..." />
  }

  const isConfirmed = booking?.status === 'confirmed'

  return (
    <div
      style={{
        maxWidth: 560,
        margin: '0 auto',
        textAlign: 'center',
        padding: '48px 24px',
      }}
    >
      {/* Success Icon */}
      <div
        style={{
          color: '#22c55e',
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CheckCircle size={64} />
      </div>

      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
        Payment Successful!
      </h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32, fontSize: 15 }}>
        {sessionId && (
          <span style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
            Checkout ID: {sessionId}
          </span>
        )}
        Your interview session has been booked. You'll receive a confirmation
        email shortly.
      </p>

      {/* Booking Details Card */}
      {booking ? (
        <div
          className="card"
          style={{ textAlign: 'left', marginBottom: 24 }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 600,
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--primary-navy)',
            }}
          >
            <Calendar size={18} />
            Booking Details
          </h3>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              fontSize: 14,
            }}
          >
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Calendar
                size={15}
                style={{ color: 'var(--text-muted)', flexShrink: 0 }}
              />
              <span>
                {new Date(booking.scheduled_start_utc).toLocaleDateString(
                  'en-US',
                  {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  }
                )}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <Clock
                size={15}
                style={{ color: 'var(--text-muted)', flexShrink: 0 }}
              />
              <span>
                {new Date(booking.scheduled_start_utc).toLocaleTimeString(
                  'en-US',
                  { hour: 'numeric', minute: '2-digit', hour12: true }
                )}{' '}
                &middot; {booking.duration_minutes} min
              </span>
            </div>

            {booking.interviewer_alias && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'var(--primary-navy)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {booking.interviewer_alias.charAt(0).toUpperCase()}
                </span>
                <span>With: {booking.interviewer_alias}</span>
              </div>
            )}

            {/* Zoom Meeting */}
            {isConfirmed && booking.zoom_join_url ? (
              <div
                style={{
                  marginTop: 8,
                  padding: 16,
                  background: 'rgba(34, 197, 94, 0.06)',
                  borderRadius: 10,
                  border: '1px solid rgba(34, 197, 94, 0.2)',
                }}
              >
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: '#16a34a',
                    marginBottom: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Video size={15} />
                  Zoom Meeting Ready
                </div>
                {booking.zoom_password && (
                  <div
                    style={{
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      marginBottom: 12,
                    }}
                  >
                    Password:{' '}
                    <strong style={{ color: 'var(--text-dark)' }}>
                      {booking.zoom_password}
                    </strong>
                  </div>
                )}
                <a
                  href={booking.zoom_join_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                  style={{ display: 'inline-flex', textDecoration: 'none', fontSize: 14 }}
                >
                  <Video size={16} />
                  Join Zoom Meeting
                </a>
              </div>
            ) : (
              <div
                style={{
                  marginTop: 8,
                  padding: 14,
                  background: 'rgba(59, 130, 246, 0.06)',
                  borderRadius: 10,
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  fontSize: 13,
                  color: '#2563eb',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}
                >
                  <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    Your Zoom meeting link will appear here once your booking
                    is confirmed (usually within a minute).
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={checking}
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'none',
                    border: 'none',
                    color: checking ? 'var(--text-muted)' : '#2563eb',
                    cursor: checking ? 'default' : 'pointer',
                    fontSize: 13,
                    padding: 0,
                    fontWeight: 500,
                  }}
                >
                  <RefreshCw
                    size={13}
                    style={{
                      animation: checking ? 'spin 0.8s linear infinite' : 'none',
                    }}
                  />
                  {checking ? 'Checking...' : 'Refresh status'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          className="card"
          style={{
            marginBottom: 24,
            padding: 20,
            background: 'rgba(59, 130, 246, 0.06)',
            border: '1px solid rgba(59, 130, 246, 0.2)',
          }}
        >
          <p style={{ fontSize: 14, color: '#2563eb', margin: 0 }}>
            Your booking is being processed. Check{' '}
            <button
              type="button"
              onClick={() => navigate('/client/interviews')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                cursor: 'pointer',
                fontWeight: 600,
                padding: 0,
                textDecoration: 'underline',
                fontSize: 14,
              }}
            >
              My Interviews
            </button>{' '}
            in a moment for details.
          </p>
        </div>
      )}

      {/* Add to Calendar */}
      {booking && (
        <div className="ps-cal-card">
          <div className="ps-cal-header">
            <CalendarPlus size={20} className="ps-cal-icon" />
            <div>
              <h3 className="ps-cal-title">Add to Calendar</h3>
              <p className="ps-cal-sub">Never miss your interview — add a reminder now</p>
            </div>
          </div>

          <div className="ps-cal-buttons">
            {/* Google Calendar */}
            <a
              href={buildGoogleCalendarUrl(booking)}
              target="_blank"
              rel="noopener noreferrer"
              className="ps-cal-btn ps-cal-btn-google"
            >
              {/* Google "G" coloured icon */}
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.36-8.16 2.36-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              Add to Google Calendar
            </a>

            {/* ICS / Apple / Outlook */}
            <button
              type="button"
              className="ps-cal-btn ps-cal-btn-ics"
              onClick={() => downloadICS(booking)}
            >
              <Download size={16} />
              Download .ics
              <span className="ps-cal-btn-sub">Apple / Outlook / Other</span>
            </button>
          </div>

          <p className="ps-cal-note">
            A 30-minute and 10-minute reminder will be included automatically.
          </p>
        </div>
      )}

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}
      >
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate('/client/interviews')}
        >
          <Calendar size={16} />
          My Interviews
        </button>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => navigate('/client/schedule')}
        >
          Schedule Another
        </button>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── Add to Calendar card ── */
        .ps-cal-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 20px 22px;
          margin-bottom: 20px;
          text-align: left;
        }

        .ps-cal-header {
          display: flex;
          align-items: flex-start;
          gap: 14px;
          margin-bottom: 18px;
        }

        .ps-cal-icon {
          color: var(--accent-gold, #d4af37);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .ps-cal-title {
          font-size: 15px;
          font-weight: 700;
          color: var(--text-dark);
          margin: 0 0 3px;
        }

        .ps-cal-sub {
          font-size: 12.5px;
          color: var(--text-muted);
          margin: 0;
        }

        .ps-cal-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .ps-cal-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 12px 16px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.18s;
          text-decoration: none;
          border: none;
        }

        .ps-cal-btn-google {
          background: #fff;
          border: 1.5px solid #dadce0 !important;
          color: #3c4043;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
        }

        .ps-cal-btn-google:hover {
          background: #f8f9fa;
          box-shadow: 0 2px 8px rgba(0,0,0,0.12);
          border-color: #bdc1c6 !important;
        }

        .ps-cal-btn-ics {
          background: #f3f4f6;
          border: 1.5px solid #e5e7eb !important;
          color: var(--text-dark);
          flex-wrap: wrap;
        }

        .ps-cal-btn-ics:hover {
          background: #e9eaec;
        }

        .ps-cal-btn-sub {
          font-size: 11.5px;
          font-weight: 400;
          color: var(--text-muted);
          margin-left: auto;
        }

        .ps-cal-note {
          margin: 14px 0 0;
          font-size: 11.5px;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .ps-cal-note::before {
          content: '⏰';
          font-size: 12px;
        }
      `}</style>
    </div>
  )
}
